import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiAlertTriangle, FiWifi, FiLock, FiPhone, 
  FiNavigation, FiHome, FiMail, FiUser, FiAlertCircle,
  FiEdit2, FiTrash2, FiPlus, FiSave, FiX, FiMove
} from 'react-icons/fi';
import { IoCarOutline } from 'react-icons/io5';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import { api } from '../../api';
import toast from 'react-hot-toast';

// Extend Quill's Link format to allow mailto: and tel: protocols
const QuillLink = Quill.import('formats/link');
class CustomLink extends QuillLink {
  static sanitize(url) {
    const sanitizedUrl = super.sanitize(url);
    if (!sanitizedUrl) {
      // Allow mailto: and tel: protocols
      if (url.startsWith('mailto:') || url.startsWith('tel:')) {
        return url;
      }
    }
    return sanitizedUrl;
  }
}
Quill.register(CustomLink, true);

const iconOptions = [
  { value: 'alert', label: 'Alert', icon: FiAlertTriangle },
  { value: 'wifi', label: 'WiFi', icon: FiWifi },
  { value: 'lock', label: 'Lock', icon: FiLock },
  { value: 'phone', label: 'Phone', icon: FiPhone },
  { value: 'navigation', label: 'Navigation', icon: FiNavigation },
  { value: 'home', label: 'Home', icon: FiHome },
  { value: 'mail', label: 'Mail', icon: FiMail },
  { value: 'user', label: 'User', icon: FiUser },
  { value: 'car', label: 'Vehicle', icon: IoCarOutline },
];

const iconMap = {
  alert: FiAlertTriangle,
  wifi: FiWifi,
  lock: FiLock,
  phone: FiPhone,
  navigation: FiNavigation,
  home: FiHome,
  mail: FiMail,
  user: FiUser,
  car: IoCarOutline,
  vehicle: IoCarOutline,
};

const QuickInfoForm = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    content: item?.content || '',
    icon: item?.icon || 'alert',
    priority: item?.priority || 2,
    is_rich_text: item?.is_rich_text || false,
    is_active: item?.is_active !== undefined ? item.is_active : true
  });
  const [useRichText, setUseRichText] = useState(item?.is_rich_text || false);
  const [showSourceCode, setShowSourceCode] = useState(false);
  const [sourceContent, setSourceContent] = useState(item?.content || '');
  const quillRef = useRef(null);

  // Custom image handler for ReactQuill
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        const loadingToast = toast.loading('Uploading image...');
        const response = await api.uploadMedia(file);
        const imageUrl = response.data.url;

        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);
        }

        toast.dismiss(loadingToast);
        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Image upload failed:', error);
        toast.error('Failed to upload image');
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalContent = showSourceCode ? sourceContent : formData.content;
    onSave({
      ...formData,
      content: finalContent,
      is_rich_text: useRichText || showSourceCode
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-display text-lodge-brown-800">
            {item ? 'Edit Quick Info' : 'Add Quick Info'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                required
                placeholder="e.g., WiFi Information"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority Level
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              >
                <option value={1}>Priority 1 - Critical (Emergency, WiFi, Check-in)</option>
                <option value={2}>Priority 2 - Important (Garage, House Rules, Vehicles)</option>
                <option value={3}>Priority 3 - Helpful (Activities, Local Info)</option>
              </select>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Content
                </label>
                <div className="flex items-center space-x-2">
                  {useRichText && (
                    <button
                      type="button"
                      onClick={() => {
                        if (showSourceCode) {
                          // Switching back to WYSIWYG - save source content
                          setFormData({ ...formData, content: sourceContent });
                        } else {
                          // Switching to source - load current content
                          setSourceContent(formData.content);
                        }
                        setShowSourceCode(!showSourceCode);
                      }}
                      className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      {showSourceCode ? 'Visual Editor' : 'Source Code'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setUseRichText(!useRichText);
                      setShowSourceCode(false);
                    }}
                    className="text-sm text-lodge-brown-600 hover:text-lodge-brown-800"
                  >
                    {useRichText ? 'Switch to Plain Text' : 'Switch to Rich Text'}
                  </button>
                </div>
              </div>
              {useRichText ? (
                showSourceCode ? (
                  <div>
                    <textarea
                      value={sourceContent}
                      onChange={(e) => setSourceContent(e.target.value)}
                      className="w-full h-48 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                      placeholder="Enter HTML content here..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Edit raw HTML. Use &lt;a href="mailto:email@example.com"&gt; for email links or &lt;a href="tel:+1234567890"&gt; for phone links.
                    </p>
                  </div>
                ) : (
                  <ReactQuill
                    ref={quillRef}
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    className="bg-white rounded-lg"
                    modules={{
                      toolbar: {
                        container: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['link', 'image'],
                          ['clean']
                        ],
                        handlers: {
                          image: imageHandler
                        }
                      }
                    }}
                    placeholder="Use bold for emphasis, bullet points for lists..."
                  />
                )
              ) : (
                <>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                    rows="4"
                    required
                    placeholder="e.g., Network: kleinmanbc&#10;Password: wendy001"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use line breaks for multiple lines of content
                  </p>
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon
              </label>
              <div className="grid grid-cols-4 gap-2">
                {iconOptions.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: value })}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center space-y-1 transition-colors ${
                      formData.icon === value
                        ? 'border-lodge-brown-500 bg-lodge-brown-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="mr-2 rounded text-lodge-brown-600 focus:ring-lodge-brown-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Show on homepage
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center"
            >
              <FiSave className="mr-2" />
              {item ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const AdminQuickInfo = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { data: itemsResponse, isLoading } = useQuery(
    'adminQuickInfoItems',
    () => api.getAdminQuickInfoItems()
  );
  const items = itemsResponse?.data || [];

  const createMutation = useMutation(
    (data) => api.createQuickInfoItem(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminQuickInfoItems');
        queryClient.invalidateQueries('quickInfoItems');
        toast.success('Quick info item created successfully');
        setShowForm(false);
      },
      onError: () => toast.error('Failed to create quick info item')
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.updateQuickInfoItem(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminQuickInfoItems');
        queryClient.invalidateQueries('quickInfoItems');
        toast.success('Quick info item updated successfully');
        setShowForm(false);
        setEditingItem(null);
      },
      onError: () => toast.error('Failed to update quick info item')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.deleteQuickInfoItem(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminQuickInfoItems');
        queryClient.invalidateQueries('quickInfoItems');
        toast.success('Quick info item deleted successfully');
      },
      onError: () => toast.error('Failed to delete quick info item')
    }
  );

  const reorderMutation = useMutation(
    (items) => api.reorderQuickInfoItems(items),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminQuickInfoItems');
        queryClient.invalidateQueries('quickInfoItems');
        toast.success('Items reordered successfully');
      },
      onError: () => toast.error('Failed to reorder items')
    }
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);

    const itemsWithNewOrder = reorderedItems.map((item, index) => ({
      id: item.id,
      order_index: index + 1
    }));

    reorderMutation.mutate(itemsWithNewOrder);
  };

  const handleSave = (formData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      const order_index = items.length > 0 
        ? Math.max(...items.map(item => item.order_index || 0)) + 1 
        : 1;
      createMutation.mutate({ ...formData, order_index });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this quick info item?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-display text-lodge-brown-800">Quick Info Management</h1>
        <button
          onClick={() => {
            setEditingItem(null);
            setShowForm(true);
          }}
          className="btn-primary flex items-center"
        >
          <FiPlus className="mr-2" />
          Add Quick Info
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Drag and drop items to reorder them. Active items will appear on the homepage.
          </p>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No quick info items yet</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary mt-4"
            >
              Add Your First Item
            </button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="quick-info-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-3"
                >
                  {items.map((item, index) => {
                    const Icon = iconMap[item.icon] || FiAlertCircle;
                    return (
                      <Draggable
                        key={item.id}
                        draggableId={String(item.id)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${
                              snapshot.isDragging ? 'shadow-lg' : ''
                            }`}
                          >
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className={`flex items-center justify-between p-4 rounded-lg border ${
                                item.is_active
                                  ? 'bg-white border-gray-200'
                                  : 'bg-gray-50 border-gray-200 opacity-60'
                              }`}
                            >
                              <div className="flex items-center space-x-4">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-move text-gray-400 hover:text-gray-600"
                                >
                                  <FiMove className="w-5 h-5" />
                                </div>
                                <div className="w-10 h-10 bg-lodge-brown-100 rounded-lg flex items-center justify-center">
                                  <Icon className="w-5 h-5 text-lodge-brown-700" />
                                </div>
                                <div>
                                  <h3 className="font-medium text-mountain-gray-800 flex items-center">
                                    {item.title}
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                      item.priority === 1 ? 'bg-red-100 text-red-700' :
                                      item.priority === 3 ? 'bg-gray-100 text-gray-600' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      P{item.priority || 2}
                                    </span>
                                    {!item.is_active && (
                                      <span className="ml-2 text-xs text-gray-500">(Hidden)</span>
                                    )}
                                  </h3>
                                  {item.is_rich_text ? (
                                    <div 
                                      className="text-sm text-mountain-gray-600 prose-sm max-w-none"
                                      dangerouslySetInnerHTML={{ __html: item.content }}
                                    />
                                  ) : (
                                    <p className="text-sm text-mountain-gray-600 whitespace-pre-wrap">
                                      {item.content}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    setEditingItem(item);
                                    setShowForm(true);
                                  }}
                                  className="text-lodge-brown-600 hover:text-lodge-brown-900 p-2"
                                >
                                  <FiEdit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="text-red-600 hover:text-red-900 p-2"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </motion.div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <AnimatePresence>
        {showForm && (
          <QuickInfoForm
            item={editingItem}
            onClose={() => {
              setShowForm(false);
              setEditingItem(null);
            }}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminQuickInfo;